/**
 * slashImageBlock.js
 *
 * Makes the slash-hero section height dynamic based on content.
 *
 * At desktop (lg-xl / 1280px+):
 *  - The .wrap height is determined by its content (.data)
 *  - The image height is synced to that value via JS
 *  - The .desktop-1440 SVG gradient (height, viewBox, path, gradient coords)
 *    is updated so the gradient line stays parallel to the clip-path diagonal
 *
 * Geometry note:
 *  The clip-path cut goes from (346px, 0) → (0, sectionHeight) on the image.
 *  The SVG gradient line mirrors that angle. Because the SVG always spans the
 *  full section height, the x-endpoints stay approximately constant (≈350 → ≈3.5)
 *  regardless of height. Only the y-endpoint and gradient coords need updating.
 *  The SVG `left: -20px` CSS offset stays correct for all heights.
 */

const BREAKPOINT_LG_XL = 1280;

// Original SVG design values (height 604px section)
const SVG_ORIG_HEIGHT = 604;
const SVG_WIDTH = 353;
const LINE_X_TOP = 350;
const LINE_Y_TOP = 2;
const LINE_X_BOTTOM = 3.5;
// Gradient original coords (userSpaceOnUse, scaled proportionally to height)
const GRAD_Y1_RATIO = 602.215 / SVG_ORIG_HEIGHT; // ≈ 0.9970 (bottom anchor)
const GRAD_Y2_RATIO = 44.6251 / SVG_ORIG_HEIGHT; // ≈ 0.0739 (top anchor)

function adjustSection(section) {
    const contentWrap = section.querySelector(".content-custom-wrap");
    const svg = section.querySelector(".desktop-1440");
    const img = section.querySelector("img.mobile");

    if (!contentWrap || !svg || !img) return;

    if (window.innerWidth < BREAKPOINT_LG_XL) {
        // Reset any JS-applied styles so CSS media queries take over
        img.style.height = "";
        return;
    }

    // Temporarily clear JS height so we can measure the natural content height
    img.style.height = "";

    // Measure the full wrapper height — this includes any vspace padding classes
    // applied to .content-custom-wrap, which .wrap alone does not reflect.
    const sectionHeight = Math.round(contentWrap.getBoundingClientRect().height);
    // Sync image height to content height
    img.style.height = sectionHeight + "px";

    // Update the SVG so the gradient line stays parallel to the clip-path cut
    updateSVG(svg, sectionHeight);
}

function updateSVG(svg, height) {
    // 1. Resize the SVG element
    svg.setAttribute("height", height);
    svg.setAttribute("viewBox", `0 0 ${SVG_WIDTH} ${height}`);

    // 2. Update the path: top → bottom, same x endpoints, y endpoint = new height
    const path = svg.querySelector("path");
    if (path) {
        path.setAttribute("d", `M${LINE_X_TOP} ${LINE_Y_TOP}L${LINE_X_BOTTOM} ${(height - LINE_Y_TOP).toFixed(3)}`);
    }

    // 3. Update gradient coords (userSpaceOnUse — must scale with SVG height)
    const gradient = svg.querySelector("linearGradient");
    if (gradient) {
        gradient.setAttribute("y1", (GRAD_Y1_RATIO * height).toFixed(4));
        gradient.setAttribute("y2", (GRAD_Y2_RATIO * height).toFixed(4));
        // x1 / x2 are horizontal and don't depend on height
    }
}

function init() {
    const sections = document.querySelectorAll("section.slash-hero");
    if (!sections.length) return;

    function adjustAll() {
        sections.forEach(section => adjustSection(section));
    }

    // First pass on DOM ready
    adjustAll();

    // Re-run after all resources load (images, iframes, etc.)
    // If load already fired (late-executing scripts), run immediately.
    if (document.readyState === "complete") {
        adjustAll();
    } else {
        window.addEventListener("load", adjustAll);
    }

    // Re-run after web fonts are rendered — the most common cause of a wrong
    // initial height, since custom title fonts reflow the text after DOM ready.
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(adjustAll);
    }

    // Re-adjust on resize (debounced)
    let resizeTimer;
    window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(adjustAll, 100);
    });
}

export default { init };
